import React, { useEffect, useState } from "react";
import { Phone, Mail, Clock, Send, Building2, TrendingUp, Handshake, MessageCircle, CheckCircle2, User, AtSign, MessageSquare } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    els.forEach((el) => el.classList.remove('visible'));
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -20px 0px' }
    );
    const timer = setTimeout(() => els.forEach((el) => observer.observe(el)), 50);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, []);
}

const quickContactIcons = [Building2, Phone, Mail, Clock];
const quickContactColors = [
  { icon: "text-primary-600", bg: "bg-primary-50" },
  { icon: "text-secondary-600", bg: "bg-secondary-50" },
  { icon: "text-rose-500", bg: "bg-rose-50" },
  { icon: "text-amber-500", bg: "bg-amber-50" },
];

const departmentIcons = [TrendingUp, Handshake, MessageCircle];
const departmentColors = [
  { icon: "text-violet-600", bg: "from-violet-500 to-purple-600" },
  { icon: "text-secondary-600", bg: "from-secondary-500 to-primary-500" },
  { icon: "text-emerald-600", bg: "from-emerald-500 to-teal-600" },
];

const Contact = () => {
  const { t } = useLanguage();
  const c = t.contact;
  useScrollReveal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("success");
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  const quickContact = [
    {
      label: c.info.address.label,
      lines: [c.info.address.line1, c.info.address.line2, c.info.address.line3],
    },
    {
      label: c.info.phone.label,
      lines: [c.info.phone.number, c.info.phone.note],
      href: "tel:" + c.info.phone.number.replace(/\s/g, ""),
    },
    {
      label: c.info.email.label,
      lines: [c.info.email.general],
      href: "mailto:" + c.info.email.general,
    },
    {
      label: c.info.hours.label,
      lines: [c.info.hours.weekdays, c.info.hours.sunday],
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-20"></div>
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-secondary-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-10 w-80 h-80 bg-primary-400/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="reveal inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-white/80 text-sm font-medium mb-6 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 inline-block"></span>
            {c.hero.badge}
            <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 inline-block"></span>
          </div>
          <h1 className="reveal stagger-1 font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
            {c.hero.title} <span className="text-secondary-400">{c.hero.titleHighlight}</span>
          </h1>
          <p className="reveal stagger-2 text-white/70 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">{c.hero.subtitle}</p>
        </div>
      </section>

      {/* Quick Contact — bento cards overlapping the hero for a layered, modern feel */}
      <section className="relative bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 lg:-mt-16 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {quickContact.map((item, i) => {
              const Icon = quickContactIcons[i];
              const colors = quickContactColors[i];
              const stagger = `stagger-${i + 1}`;
              return (
                <div key={i} className={`reveal ${stagger} bg-white rounded-2xl p-5 lg:p-6 shadow-lg shadow-gray-200/60 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}>
                  <div className={`w-11 h-11 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1.5">{item.label}</div>
                  {item.href ? (
                    <a href={item.href} className="text-gray-900 text-sm font-semibold hover:text-primary-600 transition-colors block">{item.lines[0]}</a>
                  ) : (
                    <div className="text-gray-900 text-sm font-semibold">{item.lines[0]}</div>
                  )}
                  {item.lines.slice(1).map((line, j) => (
                    <div key={j} className="text-gray-500 text-xs mt-0.5">{line}</div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">

            {/* Departments */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="reveal font-heading text-xl font-bold text-gray-900 mb-2">Talk to the Right Team</h2>
              {c.departments.map((dept: any, i: number) => {
                const Icon = departmentIcons[i] || MessageCircle;
                const colors = departmentColors[i] || departmentColors[2];
                const stagger = `stagger-${i + 1}`;
                return (
                  <div key={i} className={`reveal ${stagger} group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${colors.bg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-heading font-bold text-gray-900 text-sm mb-1">{dept.title}</h4>
                        <a href={"mailto:" + dept.email} className="text-primary-600 text-xs font-medium hover:underline break-all">{dept.email}</a>
                        <p className="text-gray-500 text-xs mt-2 leading-relaxed">{dept.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="relative bg-white rounded-3xl p-8 lg:p-10 shadow-xl shadow-gray-200/60 border border-gray-100 overflow-hidden">
                <div className="absolute -top-16 -right-16 w-48 h-48 bg-primary-50 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-secondary-50 rounded-full blur-3xl"></div>

                <div className="relative">
                  <h2 className="reveal font-heading text-2xl font-bold text-gray-900 mb-1">{c.form.title}</h2>
                  <p className="reveal text-gray-500 text-sm mb-6">We typically reply within 24 hours.</p>

                  {status === "success" && (
                    <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>{c.form.success}</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">{c.form.name} *</label>
                        <div className="relative">
                          <User className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
                          <input type="text" name="name" value={form.name} onChange={handleChange} required className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">{c.form.email} *</label>
                        <div className="relative">
                          <AtSign className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
                          <input type="email" name="email" value={form.email} onChange={handleChange} required className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm" />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">{c.form.phone}</label>
                        <div className="relative">
                          <Phone className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
                          <input type="tel" name="phone" value={form.phone} onChange={handleChange} className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">{c.form.subject}</label>
                        <select name="subject" value={form.subject} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm bg-white">
                          <option value="">Select a subject</option>
                          {c.form.subjectOptions.map((opt: string, i: number) => (
                            <option key={i} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{c.form.message} *</label>
                      <div className="relative">
                        <MessageSquare className="w-4 h-4 text-gray-400 absolute left-4 top-3.5" />
                        <textarea name="message" value={form.message} onChange={handleChange} required rows={5} className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm resize-none"></textarea>
                      </div>
                    </div>
                    <button type="submit" className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:shadow-xl hover:-translate-y-0.5 text-white font-semibold py-3.5 px-8 rounded-xl transition-all duration-300 shadow-lg">
                      <Send className="w-4 h-4" /> {c.form.submit}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
